import React from 'react';
import { Typography, Box } from '@mui/material';
import { MuiDialogue } from '@ace/ace-ui-web';

// Redux
import { useAppSelector, useAppDispatch } from '../../redux';
import { selectError, clearError } from '../../redux/ErrorReducer/errorSlice';

// Utils
import { getErrorNotification, getUserFriendlyErrorMessage } from '../../utils/errorUtils';
import lang from '../../languages/en.json';

/**
 * ErrorNotification Component
 *
 * Global error notification component that displays errors from Redux store
 * as Material-UI Dialog notifications. Automatically subscribes to error
 * state and displays user-friendly error messages.
 *
 * Features:
 * - Automatic error display from Redux store
 * - User-friendly error messages based on HTTP status codes
 * - Manual dismissal via close button
 * - Color-coded severity levels (error, warning, info)
 *
 * @returns JSX element representing the error notification
 */
const ErrorNotification: React.FC = () => {
  const dispatch = useAppDispatch();
  const error = useAppSelector(selectError);

  // Convert error to notification config
  const notification = React.useMemo(() => getErrorNotification(error), [error]);

  /**
   * Handles closing the error notification
   * Clears the error from Redux store
   */
  const handleClose = React.useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Get user-friendly message if error exists
  const userFriendlyMessage = React.useMemo(() => {
    if (!error) return '';
    const message = getUserFriendlyErrorMessage(error);
    return message || lang.errorNotification.defaultMessage;
  }, [error]);

  return (
    <MuiDialogue
      open={notification.isVisible}
      hideTriggerButton
      dialogTitle={notification.title}
      onClose={handleClose}
      dialogContent={
        <Box>
          <Typography
            variant="body1"
            gutterBottom>
            {userFriendlyMessage}
          </Typography>
          {notification.status !== undefined && notification.status !== null && (
            <Box sx={{ mt: 2, color: 'text.secondary' }}>
              <Typography variant="body2">
                {lang.errorNotification.errorCodeLabel} {notification.status}
              </Typography>
            </Box>
          )}
        </Box>
      }
    />
  );
};

export default ErrorNotification;
